/**
 * ==========================================================================
 * SEZNIK — SCROLL-DRIVEN PRODUCT CARD MOTION ENGINE (JAVASCRIPT)
 * Dynamic Left/Right Parallax & 3D Velocity Sway on Page Scroll
 * ==========================================================================
 */

(function () {
  'use strict';

  class SeznikScrollCardMotion {
    constructor() {
      this.cards = [];
      this.inViewCards = new Set();

      // Physics State
      this.lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
      this.currentScrollY = this.lastScrollY;
      this.scrollVelocity = 0;
      this.smoothVelocity = 0;
      this.lastTime = performance.now();
      this.isScrolling = false;
      this.scrollTimeout = null;

      this.init();
    }

    init() {
      this.collectCards();
      this.setupIntersectionObserver();
      this.bindEvents();

      // Start 60fps physics motion loop
      this.rafId = requestAnimationFrame((now) => this.renderLoop(now));
    }

    collectCards() {
      const selectors = [
        '.product-grid .grid__item',
        '.collection .grid__item',
        '.featured-collection .grid__item',
        '.collection__grid .grid__item',
        '.card-wrapper.product-card-wrapper',
        '.seznik-feature-card',
        '.top-selling-grid .grid__item',
        '.deals-grid .grid__item',
        '.featured-collection-grid .grid__item',
        '[data-product-card]'
      ];

      const foundElements = document.querySelectorAll(selectors.join(', '));
      const newCards = [];

      foundElements.forEach((el, index) => {
        // Exclude elements inside horizontal marquees / slideshows
        if (el.closest('.seznik-prod-marquee') || el.closest('.slideshow') || el.closest('.pm-stage')) {
          return;
        }

        el.classList.add('seznik-scroll-card-motion');
        newCards.push({
          element: el,
          index: index,
          direction: (index % 2 === 0) ? -1 : 1 // Alternating left/right parity
        });
      });

      this.cards = newCards;
      this.updateCardDirections();
    }

    updateCardDirections() {
      // Group cards by row to give left columns negative direction and right columns positive direction
      const rowMap = new Map();

      this.cards.forEach((cardObj) => {
        const rect = cardObj.element.getBoundingClientRect();
        const rowKey = Math.round(rect.top / 40) * 40; // Group by approximate vertical row

        if (!rowMap.has(rowKey)) {
          rowMap.set(rowKey, []);
        }
        rowMap.get(rowKey).push(cardObj);
      });

      rowMap.forEach((rowCards) => {
        // Sort left to right
        rowCards.sort((a, b) => {
          return a.element.getBoundingClientRect().left - b.element.getBoundingClientRect().left;
        });

        const count = rowCards.length;
        rowCards.forEach((cardObj, colIdx) => {
          if (count === 1) {
            cardObj.direction = (cardObj.index % 2 === 0) ? -1 : 1;
          } else {
            // Left half sways left (-1), Right half sways right (+1)
            const normalizedCol = (colIdx / (count - 1)) * 2 - 1; // -1 on left to +1 on right
            cardObj.direction = normalizedCol !== 0 ? normalizedCol : (cardObj.index % 2 === 0 ? -1 : 1);
          }
        });
      });
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: activate all
        this.cards.forEach((c) => this.inViewCards.add(c));
        return;
      }

      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const cardObj = this.cards.find((c) => c.element === entry.target);
            if (cardObj) {
              if (entry.isIntersecting) {
                this.inViewCards.add(cardObj);
              } else {
                this.inViewCards.delete(cardObj);
                // Reset transform when out of view
                cardObj.element.style.transform = '';
              }
            }
          });
        },
        {
          rootMargin: '100px 0px 100px 0px',
          threshold: 0.01
        }
      );

      this.cards.forEach((cardObj) => {
        this.observer.observe(cardObj.element);
      });
    }

    bindEvents() {
      // Window Scroll with passive listener
      window.addEventListener('scroll', () => {
        const currentY = window.pageYOffset || document.documentElement.scrollTop;
        const now = performance.now();
        const dt = Math.max(now - this.lastTime, 8);

        // Calculate velocity (pixels per frame)
        const delta = currentY - this.lastScrollY;
        this.scrollVelocity = delta / (dt / 16.67);

        this.lastScrollY = currentY;
        this.lastTime = now;
        this.isScrolling = true;

        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
          this.scrollVelocity = 0;
        }, 120);
      }, { passive: true });

      // Window Resize
      window.addEventListener('resize', () => {
        this.updateCardDirections();
      }, { passive: true });

      // Re-scan when Shopify section or pagination loads
      document.addEventListener('shopify:section:load', () => this.refresh());
      document.addEventListener('collection:reloaded', () => this.refresh());
      window.addEventListener('DOMContentLoaded', () => this.refresh());
    }

    refresh() {
      this.collectCards();
      this.setupIntersectionObserver();
    }

    renderLoop(now) {
      // Smooth lerp on scroll velocity
      const targetVel = this.isScrolling ? this.scrollVelocity : 0;
      this.smoothVelocity += (targetVel - this.smoothVelocity) * 0.14;

      // Only calculate if there's active velocity or cards in view
      if (Math.abs(this.smoothVelocity) > 0.05 || this.isScrolling) {
        this.updateCardsMotion();
      } else if (this.smoothVelocity !== 0) {
        this.smoothVelocity = 0;
        this.resetInViewCards();
      }

      this.rafId = requestAnimationFrame((t) => this.renderLoop(t));
    }

    updateCardsMotion() {
      const isMobile = window.innerWidth <= 768;
      const maxShift = isMobile ? 12 : 24;
      const velMultiplier = isMobile ? 0.35 : 0.65;
      const vHeight = window.innerHeight;
      const vCenter = vHeight / 2;

      this.inViewCards.forEach((cardObj) => {
        const el = cardObj.element;

        // Skip if hovered so user has clean clickable interaction
        if (el.matches(':hover')) return;

        const rect = el.getBoundingClientRect();
        const cardCenterY = rect.top + rect.height / 2;
        
        // Relative vertical progress in viewport (-1 at top, 0 in center, +1 at bottom)
        const relY = (cardCenterY - vCenter) / vCenter;

        // Left/Right Motion Calculation:
        // Horizontal offset based on scroll velocity + alternating direction
        const shiftX = Math.max(-maxShift, Math.min(maxShift, this.smoothVelocity * cardObj.direction * velMultiplier));
        
        // Subtle 3D Yaw & Roll angle
        const rotY = Math.max(-4.5, Math.min(4.5, -this.smoothVelocity * cardObj.direction * 0.12));
        const rotZ = Math.max(-3.0, Math.min(3.0, this.smoothVelocity * cardObj.direction * 0.06));

        el.style.transform = `translate3d(${shiftX.toFixed(1)}px, 0, 0) rotateY(${rotY.toFixed(2)}deg) rotateZ(${rotZ.toFixed(2)}deg)`;
      });
    }

    resetInViewCards() {
      this.inViewCards.forEach((cardObj) => {
        if (!cardObj.element.matches(':hover')) {
          cardObj.element.style.transform = 'translate3d(0, 0, 0) rotateY(0deg) rotateZ(0deg)';
        }
      });
    }

    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.observer) this.observer.disconnect();
    }
  }

  // Initialize Motion Engine
  function initScrollMotion() {
    window.seznikScrollCardMotion = new SeznikScrollCardMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollMotion);
  } else {
    initScrollMotion();
  }
})();
